import { useContext } from 'react';
import { graphql } from 'gatsby';
import KontentImage from '@/components/KontentImage/KontentImage';
import InfoTag from '@/components/InfoTag/InfoTag';
import useLinkProps from '../Link/hooks/useLinkProps';
import { applyPaddingCSS } from '@/utils/generatePadding';
import type { BlockHTMLId, BlockHTMLClassName } from '@/utils/renderBlock';
import { GeneralContext } from '@/context/GeneralContext';
import {
  Container,
  ButtonWrapper,
  InfoTagWrapper,
  StyledSection,
  StyledLink,
} from './AnnouncementButton.styles';

export const TIDELIFT_KEY = 'TIDELIFT-VISITOR-ONLY';

const AnnouncementButton = ({
  announcementButtonElements,
  system,
  id,
  className,
}: Queries.ANNOUNCEMENT_BUTTONFragment & BlockHTMLId & BlockHTMLClassName) => {
  const { icon, button, info_tag, background_color } = announcementButtonElements;
  const { linkChildren, linkProps } = useLinkProps({
    action: button.value.linkElements.action.value,
    target: button.value.linkElements.target?.value,
    event: button.value.linkElements.event?.value,
  });

  const hasIcon = !!icon?.value?.url;
  const hasInfoTag = !!info_tag?.value?.system.id;
  const buttonText = button.value.linkElements.text.value;

  // Per ticket https://sonarsource.atlassian.net/browse/SONARSITE-2682
  // The AnnoucementButton will have an alternate apperance based on the presence of the TIDELIFT_KEY in the system name...
  // and a cookie set in Context by users who arrive from the Tidelift website.
  const { isTideliftUser } = useContext(GeneralContext);
  const isTideliftLoginComponent = system.name.includes(TIDELIFT_KEY);
  if (isTideliftLoginComponent && isTideliftUser) {
    return (
      <StyledSection className={className} backgroundColor={background_color?.value ?? 'white'}>
        <Container
          css={applyPaddingCSS(announcementButtonElements)}
          data-codename={system.codename}
          id={id}
        >
          <a href="https://tidelift.com/login" target="_blank" rel="noopener">
            <ButtonWrapper aria-label={buttonText} hasInfoTag={true}>
              <InfoTagWrapper>
                <InfoTag
                  system={{ id: `${system.codename}-tidelift-infotag` }}
                  elements={{
                    text: { value: 'Login' },
                    color: { value: 'color2' },
                  }}
                  size="large"
                />{' '}
              </InfoTagWrapper>
              Welcome Tidelift customers. Access your account here.
            </ButtonWrapper>
          </a>
        </Container>
      </StyledSection>
    );
  }

  return (
    <StyledSection className={className} backgroundColor={background_color?.value ?? 'white'}>
      <Container
        css={applyPaddingCSS(announcementButtonElements)}
        data-codename={system.codename}
        id={id}
      >
        <StyledLink {...linkProps}>
          <ButtonWrapper aria-label={buttonText} hasInfoTag={hasInfoTag}>
            {hasInfoTag && (
              <InfoTagWrapper>
                <InfoTag {...info_tag.value} size="large" />{' '}
              </InfoTagWrapper>
            )}
            {linkChildren}
            {buttonText}
            {hasIcon && (
              <KontentImage
                src={icon.value.url}
                alt={icon.value.description ?? `Image for ${buttonText}`}
                sizes={[{ width: 16, height: 16, quality: 80 }]}
              />
            )}
          </ButtonWrapper>
        </StyledLink>
      </Container>
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment ANNOUNCEMENT_BUTTON on kontent_item_announcement_button {
    __typename
    system {
      codename
      name
    }
    announcementButtonElements: elements {
      ...PADDING
      button {
        value {
          system {
            id
          }
          ...LINK
        }
      }
      icon {
        value {
          description
          url
          width
          height
        }
      }
      info_tag {
        value {
          system {
            id
          }
          ...INFO_TAG
        }
      }
      background_color {
        value
      }
    }
  }
`;

export default AnnouncementButton;
